import React, { useState } from 'react';
import { Box, Button, TextField, MenuItem, IconButton, Paper, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Delete } from '@mui/icons-material';
import { en } from '../translations/en';

export interface Measurement {
  type: string;
  value: string;
  fraction: string;
  unit?: string; // For weight/mass/volume measurements
}

interface MeasurementFieldProps {
  dimensions?: Measurement[];
  onDimensionsChange?: (dimensions: Measurement[]) => void;
}

// Dimension categories
type DimensionCategory = 'linear' | 'weight' | 'volume';

const getDimensionCategory = (dimensionType: string): DimensionCategory => {
  // Use only translation keys for comparison to support i18n properly
  const weightTypes = [en.wizard.measure.dimensions.weight, en.wizard.measure.dimensions.mass];
  const volumeTypes = [en.wizard.measure.dimensions.volume];

  if (weightTypes.includes(dimensionType)) return 'weight';
  if (volumeTypes.includes(dimensionType)) return 'volume';
  return 'linear';
};

const getDefaultUnit = (category: DimensionCategory): string => {
  switch (category) {
    case 'weight':
      return 'lbs';
    case 'volume':
      return 'L';
    default:
      return 'inches';
  }
};

export const MeasurementField = ({
  dimensions = [{ type: 'Height', value: '', fraction: '', unit: 'inches' }, { type: 'Width', value: '', fraction: '', unit: 'inches' }],
  onDimensionsChange
}: MeasurementFieldProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedDimension, setSelectedDimension] = useState('');

  // Linear units (for distance/length measurements)
  const linearUnits = [
    { key: 'inches', label: en.wizard.measure.units.inches },
    { key: 'mm', label: en.wizard.measure.units.mm },
    { key: 'cm', label: en.wizard.measure.units.cm },
    { key: 'feet', label: en.wizard.measure.units.feet },
    { key: 'meters', label: en.wizard.measure.units.meters }
  ];

  // Weight/mass units
  const weightUnits = [
    { key: 'lbs', label: en.wizard.measure.weightUnits.lbs },
    { key: 'oz', label: en.wizard.measure.weightUnits.oz },
    { key: 'kg', label: en.wizard.measure.weightUnits.kg },
    { key: 'g', label: en.wizard.measure.weightUnits.g }
  ];

  // Volume units
  const volumeUnits = [
    { key: 'L', label: en.wizard.measure.volumeUnits.L },
    { key: 'mL', label: en.wizard.measure.volumeUnits.mL },
    { key: 'gal', label: en.wizard.measure.volumeUnits.gal },
    { key: 'fl_oz', label: en.wizard.measure.volumeUnits.fl_oz },
    { key: 'cc', label: en.wizard.measure.volumeUnits.cc }
  ];

  const dimensionOptions = [
    en.wizard.measure.dimensions.depth,
    en.wizard.measure.dimensions.diameter,
    en.wizard.measure.dimensions.length,
    en.wizard.measure.dimensions.thickness,
    en.wizard.measure.dimensions.radius,
    en.wizard.measure.dimensions.circumference,
    en.wizard.measure.dimensions.weight,
    en.wizard.measure.dimensions.mass,
    en.wizard.measure.dimensions.volume
  ];

  // Fractions for inches (1/16 through 15/16)
  const inchFractionOptions = [
    '1/16', '1/8', '3/16', '1/4', '5/16', '3/8', '7/16', '1/2',
    '9/16', '5/8', '11/16', '3/4', '13/16', '7/8', '15/16'
  ];

  // Coarser fractions for feet
  const feetFractionOptions = ['1/4', '1/3', '1/2', '2/3', '3/4'];

  // Select fraction options based on unit
  const getFractionOptions = (selectedUnit: string): string[] => {
    if (selectedUnit === 'feet') {
      return feetFractionOptions;
    }
    return inchFractionOptions;
  };

  const addDimension = () => {
    if (selectedDimension && !dimensions.find(m => m.type === selectedDimension)) {
      const category = getDimensionCategory(selectedDimension);
      const defaultUnit = getDefaultUnit(category);
      const newDimension: Measurement = {
        type: selectedDimension,
        value: '',
        fraction: '',
        unit: defaultUnit,
      };
      const newDimensions = [...dimensions, newDimension];
      onDimensionsChange?.(newDimensions);
      setSelectedDimension('');
    }
  };

  const removeDimension = (index: number) => {
    if (dimensions.length > 1) {
      const newDimensions = dimensions.filter((_, i) => i !== index);
      onDimensionsChange?.(newDimensions);
    }
  };

  const updateMeasurement = (index: number, field: keyof Measurement, value: string) => {
    const newDimensions = dimensions.map((dim, i) =>
      i === index ? { ...dim, [field]: value } : dim
    );
    onDimensionsChange?.(newDimensions);
  };

  const getUnitsForCategory = (category: DimensionCategory) => {
    switch (category) {
      case 'weight':
        return weightUnits;
      case 'volume':
        return volumeUnits;
      default:
        return linearUnits;
    }
  };

  return (
    <Box>
      {dimensions.map((measurement, index) => {
        const category = getDimensionCategory(measurement.type);
        const categoryUnits = getUnitsForCategory(category);
        const displayUnit = measurement.unit || getDefaultUnit(category);
        const showFractions = displayUnit === 'inches' || displayUnit === 'feet';

        return (
          <Paper
            key={index}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box
              data-testid="measurement-row"
              sx={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                gap: 2
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: 80,
                  color: theme.palette.text.primary
                }}
              >
                {measurement.type}
              </Typography>

              <TextField
                size="small"
                value={measurement.value}
                onChange={(e) => updateMeasurement(index, 'value', e.target.value)}
                placeholder="0"
                sx={{ width: isMobile ? '100%' : 100 }}
              />

              {showFractions && (
                <TextField
                  select
                  size="small"
                  value={measurement.fraction || ''}
                  onChange={(e) => updateMeasurement(index, 'fraction', e.target.value)}
                  sx={{ width: isMobile ? '100%' : 80 }}
                  placeholder="Fraction"
                >
                  <MenuItem value="">-</MenuItem>
                  {getFractionOptions(displayUnit).map((fraction) => (
                    <MenuItem key={fraction} value={fraction}>
                      {fraction}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                size="small"
                value={displayUnit}
                onChange={(e) => updateMeasurement(index, 'unit', e.target.value)}
                sx={{ width: isMobile ? '100%' : 120 }}
                SelectProps={{
                  native: false
                }}
                data-testid={`unit-select-${measurement.type.toLowerCase()}`}
              >
                {categoryUnits.map((unitOption) => (
                  <MenuItem key={unitOption.key} value={unitOption.key}>
                    {unitOption.label}
                  </MenuItem>
                ))}
              </TextField>

              {dimensions.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeDimension(index)}
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          </Paper>
        );
      })}

      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: isMobile ? 'flex-start' : 'center' }}>
        <TextField
          select
          size="small"
          value={selectedDimension}
          onChange={(e) => setSelectedDimension(e.target.value)}
          sx={{ width: isMobile ? '100%' : 150 }}
          placeholder="Select dimension"
          data-testid="add-dimension-select"
        >
          {dimensionOptions
            .filter(option => !dimensions.find(m => m.type === option))
            .map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
        </TextField>

        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={addDimension}
          disabled={!selectedDimension}
        >
          {en.wizard.measure.addButton}
        </Button>
      </Box>
    </Box>
  );
};
